import React from 'react';
import SEO from '../../components/ui/SEO';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { callsToAction } from '../../data/practice';
import { Brain, Activity, Scale, HeartPulse, Stethoscope, ArrowRight, FileCheck } from 'lucide-react';
export default function Services() {
  const services = [{
    title: 'Criminal Psychiatric Reports and Fitness to Plead',
    description: 'Fitness to plead and stand trial, mental state at the time of an alleged offence, and sentencing reports. Also covers Parole Board and licence-condition assessments, extradition, and assessments in prisons and secure settings.',
    icon: <Scale className="h-12 w-12" aria-hidden="true" data-id="element-1085" data-mp="2af023b19apjv" />,
    link: '/services/forensic-risk'
  }, {
    title: 'Mental Capacity Assessments for Litigation and the Court of Protection',
    description: 'Decision-specific capacity assessments under the Mental Capacity Act 2005, including litigation capacity, Court of Protection property and affairs, deputyship, contested lasting powers of attorney, and testamentary and retrospective capacity.',
    icon: <Activity className="h-12 w-12" aria-hidden="true" data-id="element-1086" data-mp="iay437tm99sz" />,
    link: '/services/mental-capacity'
  }, {
    title: 'Immigration, Employment and Family Proceedings',
    description: 'Psychiatric reports for immigration, asylum and human rights appeals, Employment Tribunal and professional regulatory proceedings, and assessment of adult parties in family proceedings.',
    icon: <Stethoscope className="h-12 w-12" aria-hidden="true" data-id="element-1087" data-mp="111fu34n2dran" />,
    link: '/services/specialist'
  }, {
    title: 'PTSD and Complex Trauma',
    description: 'Assessment of PTSD, complex PTSD and other trauma-related conditions, including Istanbul Protocol reports and cases involving torture, trafficking or modern slavery.',
    icon: <HeartPulse className="h-12 w-12" aria-hidden="true" data-id="element-1088" data-mp="3hpa96hlhj7ro" />,
    link: '/services/trauma-ptsd'
  }, {
    title: 'Clinical Negligence and Personal Injury',
    description: 'Independent evidence on psychiatric injury, causation, condition and prognosis, for claimant and defendant solicitors and as a single joint expert.',
    icon: <FileCheck className="h-12 w-12" aria-hidden="true" data-id="element-1089" data-mp="3e85snkvls1o7" />,
    link: '/services/clinical-negligence'
  }, {
    title: 'ADHD and Neurodevelopmental Assessments',
    description: 'Medico-legal assessment of ADHD and other neurodevelopmental conditions, and their relevance to criminal, civil, employment and family proceedings.',
    icon: <Brain className="h-12 w-12" aria-hidden="true" data-id="element-1090" data-mp="2qk4jc9qloh7q" />,
    link: '/services/neurodevelopmental'
  }];
  return <main className="min-h-screen bg-white" data-id="element-1091" data-mp="2vfbkb9shj7sy">
      <SEO title="Psychiatric Expert Witness Services | England and Wales" description="Forensic, capacity, PTSD, ADHD, immigration and clinical negligence psychiatric reports for legal proceedings. CPR Part 35 compliant." path="/services" keywords={['psychiatric expert witness services', 'medico-legal psychiatric assessments', 'psychiatric reports for legal proceedings']} imageAlt="Psychiatric expert witness services for legal proceedings" schema={{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Psychiatric Expert Witness Services',
      itemListElement: services.map((service, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: service.title,
        url: `https://medicolegal-psychiatry.co.uk${service.link}`
      }))
    }} data-id="element-1092" data-mp="2hhmzg1yf40nw" />
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-1093" data-mp="34kjo2anmaec2">
        <div className="container mx-auto px-4" data-id="element-1094" data-mp="34ifkjttmrhcb">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-1095" data-mp="2zm8bvt9unrqo">
            Psychiatric Expert Witness Services
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1096" data-mp="3jlqr0bbbjras">
            Dr. Syed provides comprehensive Medico-Legal services for
            solicitors, barristers, and courts, guided by clarity, compassion,
            and expertise.
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-1097" data-mp="30op9xs4hq0qh">
        <div className="container mx-auto px-4" data-id="element-1098" data-mp="30ph20syvpgc0">
          <div className="max-w-3xl mx-auto mb-12" data-id="element-1099" data-mp="30r20gtobdjf5">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1100" data-mp="34rc3pykq1vqb">
              At Dr. Imtiaz Syed's practice, we are dedicated to providing
              precise, impartial, and robust psychiatric expertise to legal
              professionals across the UK. We understand the critical role that
              expert insights play in complex legal proceedings, and our
              services are meticulously designed to deliver clarity and
              confidence to your cases.
            </p>
            <p className="text-slate-dark text-lg mb-6" data-id="element-1101" data-mp="2egjv89lbb34i">
              Dr. Syed offers a comprehensive suite of medico-legal psychiatric
              assessments and expert witness services, all delivered with the
              guiding principles of Clarity, Compassion, and Expertise. Each
              assessment is conducted with the utmost care, ensuring
              thoroughness and sensitivity, leading to reports that are not only
              scientifically sound but also practical and directly applicable to
              legal contexts.
            </p>
            <p className="text-slate-dark text-lg" data-id="element-1102" data-mp="1lrmpf5yaeysn">
              Explore our key areas of specialisation:
            </p>
          </div>
          {/* Services */}
          <div className="space-y-12 mb-16" data-id="element-1103" data-mp="1gucpuxh6p6r3">
            {services.map((service, index) => <div key={index} className="bg-white rounded-lg shadow-md p-8 border-l-4 border-primary" data-id="element-1104" data-mp="3g544e4kmmnc2" data-mpi={index}>
                <div className="flex flex-col md:flex-row gap-6" data-id="element-1105" data-mp="19gvcqi0acnfg">
                  <div className="text-primary md:w-24 flex justify-center" data-id="element-1106" data-mp="19fakx05cvila">
                    {service.icon}
                  </div>
                  <div className="flex-1" data-id="element-1107" data-mp="zjpl98nreuep">
                    <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1108" data-mp="zl0ptap1466v">
                      {service.title}
                    </h2>
                    <p className="text-slate-dark mb-6" data-id="element-1109" data-mp="1s84pr199lubs">
                      {service.description}
                    </p>
                    <Link to={service.link} className="inline-flex items-center text-primary font-medium hover:text-secondary transition-colors" data-id="element-1110" data-mp="1x5wremzpout6">
                      More on {service.title}{' '}
                      <ArrowRight className="ml-1 h-4 w-4" data-id="element-1111" data-mp="7s1fx63ef85d" />
                    </Link>
                  </div>
                </div>
              </div>)}
          </div>
          {/* Why Choose Section */}
          <div className="bg-gray-50 rounded-lg p-8 shadow-md" data-id="element-1112" data-mp="49kmsq26mem3">
            <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-1113" data-mp="4bx3kzw7eixk">
              Why Choose Dr. Imtiaz Syed for Expert Witness Services?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1114" data-mp="e6yvu4ls014d">
              Our commitment is to deliver expert medico-legal reports that are:
            </p>
            <ul className="space-y-3 mb-6 text-slate-dark" data-id="element-1115" data-mp="3hp08cr26u00v">
              <li className="flex items-start" data-id="element-1116" data-mp="3hpi9o23pj4fn">
                <span className="text-secondary font-bold mr-2" data-id="element-1117" data-mp="3cq5r3tjvcjiv">•</span>
                <span data-id="element-1118" data-mp="3cnt24wfp18wb">
                  <strong data-id="element-1119" data-mp="3rejrzyxq5xws">Robust & Objective:</strong> Clinically sound,
                  evidence-based, and impartial.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1120" data-mp="3mmsasf0gl4jb">
                <span className="text-secondary font-bold mr-2" data-id="element-1121" data-mp="2yxz7tylf5wf7">•</span>
                <span data-id="element-1122" data-mp="2yw652udxfwch">
                  <strong data-id="element-1123" data-mp="2tzxtfqyxula2">Clear & Concise:</strong> Jargon-free and directly
                  relevant to legal questions.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1124" data-mp="28i7ajrlza0ls">
                <span className="text-secondary font-bold mr-2" data-id="element-1125" data-mp="2deecl9b96ryg">•</span>
                <span data-id="element-1126" data-mp="2ieiriuwijemk">
                  <strong data-id="element-1127" data-mp="2ie0wu2pi18gr">Timely & Responsive:</strong> Standard report
                  turnaround is 5–7 working days, and Dr Syed aims to respond to
                  urgent enquiries within 1–2 days. Timing depends on the case
                  and receipt of complete instructions and records.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1128" data-mp="2wc7fiblvn1vu">
                <span className="text-secondary font-bold mr-2" data-id="element-1129" data-mp="3148nhlc0i6g2">•</span>
                <span data-id="element-1130" data-mp="3174m7bvooz5k">
                  <strong data-id="element-1131" data-mp="364d215td0r3n">Ethical & Confidential:</strong> Adhering to the
                  highest professional standards.
                </span>
              </li>
            </ul>
            <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1132" data-mp="3f4jecodbnl18">
              Discuss an Instruction
            </h3>
            <p className="text-slate-dark mb-4" data-id="element-1133" data-mp="13hg4o22u80lk">
              Send a short outline of the case and Dr Syed will confirm whether
              it falls within his expertise and availability, with a fee quote
              and timescale. Not every instruction can be accepted.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-1134" data-mp="phygnoxd8rd9">
              <Button variant="primary" href={callsToAction.discussInstruction.href} data-id="element-1135" data-mp="pjrkkwglgxhk">
                {callsToAction.discussInstruction.label}
              </Button>
              <Button variant="secondary" href={callsToAction.requestFeeQuote.href} data-id="element-1136" data-mp="1i6m3vq1odqtg">
                {callsToAction.requestFeeQuote.label}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}